import React, { useState, createContext, useEffect, useRef } from "react";

import { Modal } from "antd";

import { readDraft, writeDraft } from "./draft";

const { confirm } = Modal;

export const AddOrderDeliveryContext = createContext();

/**
 * Агуулах/салбарын блокийн id. Өмнө нь `new Date().getTime()` байсан тул нэг
 * рендерт үүсэх агуулах ба салбарын блок ижил id авч, формын талбарууд
 * (`wh-code-<id>`, `br-code-<id>`) хоорондоо хутгалддаг байсан.
 */
let blockCounter = 0;
const nextBlockId = (prefix) => `${prefix}-${new Date().getTime()}-${++blockCounter}`;

/**
 * Солих барааны (агуулах/салбар) төлөв.
 *
 * @param {string} [props.draftKey] sessionStorage-ийн key. Дамжуулбал сонгосон
 * агуулах, бараа refresh хийхэд сэргэнэ.
 */
export const AddOrderDeliveryProvider = ({ draftKey, children }) => {
  // Хуудас refresh хийхэд бөглөсөн мэдээлэл сэргэхийн тулд эхлэхдээ л уншина
  const draft = useRef(readDraft(draftKey)).current;

  const [reference, setReference] = useState(draft.reference ?? {});
  const [deliveryReference, setDeliveryReference] = useState(draft.deliveryReference ?? {});
  const [selectedWareHouses, setSelectedWareHouses] = useState(draft.selectedWareHouses ?? []);
  const [selectedBranches, setSelectedBranches] = useState(draft.selectedBranches ?? []);
  const [fromBranch, setFromBranch] = useState(draft.fromBranch ?? false);
  const [fromChoice, setFromChoice] = useState(draft.fromChoice ?? false);
  const [fromWarehouse, setFromWareHouse] = useState(draft.fromWarehouse ?? true);
  const [customFields, setCustomFields] = useState([]);
  const [bagHorooId, setBagHorooId] = useState(draft.bagHorooId);
  const [addressArray, setAddressArray] = useState(draft.addressArray ?? []);

  const [formDataStep1, setFormDataStep1] = useState(draft.formDataStep1);
  const [formDataStep2, setFormDataStep2] = useState(draft.formDataStep2);

  const [warehouseList, setWareHouseList] = useState(
    draft.warehouseList ?? [{ id: nextBlockId("wh"), products: [] }]
  );

  const [branchList, setBranchList] = useState(draft.branchList ?? []);

  // Төлөв өөрчлөгдөх бүрд draft-ыг шинэчилнэ. `customFields` нь API-аас дахин
  // татагддаг тул хадгалахгүй.
  useEffect(() => {
    writeDraft(draftKey, {
      reference,
      deliveryReference,
      selectedWareHouses,
      selectedBranches,
      fromBranch,
      fromChoice,
      fromWarehouse,
      bagHorooId,
      addressArray,
      formDataStep1,
      formDataStep2,
      warehouseList,
      branchList,
    });
  }, [
    draftKey,
    reference,
    deliveryReference,
    selectedWareHouses,
    selectedBranches,
    fromBranch,
    fromChoice,
    fromWarehouse,
    bagHorooId,
    addressArray,
    formDataStep1,
    formDataStep2,
    warehouseList,
    branchList,
  ]);

  const addWarehouse = () => {
    setWareHouseList([...warehouseList, { id: nextBlockId("wh"), products: [] }]);
  };

  const removeWarehouse = (id) => {
    confirm({
      title: "Агуулахаас татахаар сонгосон бараануудыг устгах уу?",
      okButtonProps: {
        style: {backgroundColor: '#2FA1D9'}
      },
      okText: "Тийм",
      cancelText: "Үгүй",
      onOk: () => {
        let tmp = [...warehouseList];
        setWareHouseList(tmp.filter((item) => item.id !== id));

        let tmp1 = [...selectedWareHouses];
        setSelectedWareHouses(tmp1.filter((item) => item.id !== id));
      },
    });
  };

  const addProductWarehouse = (id, products = []) => {
    let tmp = [...warehouseList];
    tmp = tmp.map((item) => {
      if (item.id === id) item.products = products;
      return item;
    });
    setWareHouseList(tmp);
  };

  const removeProductWarehouse = (wareHouseId, productId) => {
    let tmp = [...warehouseList];
    tmp = tmp.map((item) => {
      if (item.id === wareHouseId) {
        if (item.products.length) {
          const pIndex = item.products.findIndex((el) => el._id === productId);
          if (pIndex !== -1) {
            item.products.splice(pIndex, 1);
          }
        }
      }
      return item;
    });
    setWareHouseList(tmp);
  };

  const removeProductBranch = (branchId, productId) => {
    let tmp = [...branchList];
    tmp = tmp.map((item) => {
      if (item.id === branchId) {
        if (item.products.length) {
          const pIndex = item.products.findIndex((el) => el._id === productId);
          if (pIndex !== -1) {
            item.products.splice(pIndex, 1);
          }
        }
      }
      return item;
    });
    setBranchList(tmp);
  };

  const onChangeProductUnitWareHouse = (id, prodIndex, unit) => {
    let tmp = [...warehouseList];
    tmp = tmp.map((item) => {
      if (item.id === id) {
        if (item?.products?.length > prodIndex) {
          item.products[prodIndex].totalUnit = unit;
        }
      }
      return item;
    });
    setWareHouseList(tmp);
  };

  const onChangeProductUnitBranch = (id, prodIndex, unit) => {
    let tmp = [...branchList];
    tmp = tmp.map((item) => {
      if (item.id === id) {
        if (item?.products?.length > prodIndex) {
          item.products[prodIndex].totalUnit = unit;
        }
      }
      return item;
    });
    setBranchList(tmp);
  };

  const addBranch = () => {
    setBranchList([...branchList, { id: nextBlockId("br"), products: [] }]);
  };

  const removeBranch = (id) => {
    confirm({
      title: "Салбараас татахаар сонгосон бараануудыг устгах уу?",
      okText: "Тийм",
      cancelText: "Үгүй",
      okButtonProps: {
        style: {backgroundColor: '#2FA1D9'}
      },
      onOk: () => {
        let tmp = [...branchList];
        setBranchList(tmp.filter((item) => item.id !== id));

        let tmp1 = [...selectedBranches];
        setSelectedBranches(tmp1.filter((item) => item.id !== id));
      },
    });
  };

  const addProductBranch = (id, products = []) => {
    let tmp = [...branchList];
    tmp = tmp.map((item) => {
      if (item.id === id) {
        // item.products = [...item.products, ...products];
        item.products = products;
      }
      return item;
    });
    setBranchList(tmp);
  };

  const addSelectedWareHouse = (warehouse = { id: "", value: "" }) => {
    if (selectedWareHouses.find((wh) => wh?.id === warehouse.id)) {
      let tmp = selectedWareHouses.map((item) => {
        if (item.id === warehouse.id) {
          item.value = warehouse.value;
        }
        return item;
      });

      setSelectedWareHouses([...tmp]);
    } else {
      setSelectedWareHouses([...selectedWareHouses, warehouse]);
    }
  };

  const clearWareHouseProducts = (id = "") => {
    let tmp = [...warehouseList];
    tmp = tmp.map((item) => {
      if (item.id === id) {
        item.products = [];
      }
      return item;
    });
    setWareHouseList(tmp);
  };

  const addSelectedBranches = (branch = { id: "", value: "" }) => {
    if (selectedBranches.find((wh) => wh?.id === branch.id)) {
      let tmp = selectedBranches.map((item) => {
        if (item.id === branch.id) {
          item.value = branch.value;
        }
        return item;
      });
      setSelectedBranches([...tmp]);
    } else {
      setSelectedBranches([...selectedBranches, branch]);
    }
  };

  const clearSelectedWareHouses = () => setSelectedWareHouses([]);
  const clearSelectedBranches = () => setSelectedBranches([]);

  return (
    <AddOrderDeliveryContext.Provider
      value={{
        draftKey,
        reference,
        setReference,
        selectedWareHouses,
        addSelectedWareHouse,
        clearSelectedWareHouses,
        selectedBranches,
        addSelectedBranches,
        clearSelectedBranches,
        deliveryReference,
        setDeliveryReference,
        branchList,
        setBranchList,
        addBranch,
        removeBranch,
        addProductBranch,
        removeProductBranch,

        warehouseList,
        setWareHouseList,
        addWarehouse,
        removeWarehouse,
        addProductWarehouse,
        clearWareHouseProducts,
        removeProductWarehouse,

        onChangeProductUnitWareHouse,
        onChangeProductUnitBranch,

        formDataStep1,
        formDataStep2,
        setFormDataStep1,
        setFormDataStep2,

        fromBranch,
        setFromBranch,

        fromChoice,
        setFromChoice,

        fromWarehouse,
        setFromWareHouse,

        customFields,
        setCustomFields,

        addressArray,
        setAddressArray,

        bagHorooId,
        setBagHorooId,
      }}
    >
      {children}
    </AddOrderDeliveryContext.Provider>
  );
};
